import type { ErrorDetails } from "@amzn/ttechclaimintakeservice-client";

import { retrieveClaims } from "./retrievalOperations";
import type { RetrievalRequest } from "./retrievalOperations";
import type {
  ClaimRecord,
  SearchConfig,
  SearchDateRange,
  SearchState,
  SearchableField,
  TermNormalization,
} from "./types";

/**
 * Every terminal state of one submission. `results` is the only kind that means
 * a request was issued and answered; four of the remaining six are reached
 * without issuing anything at all, which is why they carry their own message
 * rather than sharing a generic failure string.
 */
export type DispatchOutcome =
  | {
      readonly kind: "results";
      readonly rows: readonly ClaimRecord[];
      readonly hasMoreResults: boolean;
    }
  | { readonly kind: "fieldErrors"; readonly errors: readonly ErrorDetails[] }
  | { readonly kind: "retrievalError" }
  | { readonly kind: "validationError"; readonly message: string }
  | { readonly kind: "unsupportedQuery"; readonly message: string }
  | { readonly kind: "configError"; readonly message: string }
  | { readonly kind: "superseded" };

export interface ExecuteSearchArgs {
  readonly state: SearchState;
  readonly config: SearchConfig;
  /**
   * The countries to scope the request to, already resolved from the selection
   * and the signed-in user's roles. The role-to-country mapping stays with the
   * page: deciding which countries a user may see is an access decision, and
   * the dispatcher sends what it is given.
   */
  readonly countries: readonly string[];
  /**
   * Present only in my-submitted-claims mode, where the config's
   * `mySubmittedClaims` block replaces the selected field: it supplies the
   * query, the alias takes the place of a search term, and there is no search
   * bar to enter one.
   */
  readonly mySubmittedClaimsAlias?: string;
}

/** Requests are abandoned rather than left to hang. */
const RETRIEVAL_TIMEOUT_MS = 10000;

const SECONDS_PER_DAY = 86400;

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** A request with everything but the signal, which `issueRequest` owns. */
type DispatchableRequest = Omit<RetrievalRequest, "signal">;

type RequestPlan =
  | { readonly kind: "dispatchable"; readonly request: DispatchableRequest }
  | { readonly kind: "refused"; readonly outcome: DispatchOutcome };

type QueryInput = Record<string, string | number | readonly string[]>;

/**
 * The in-flight request's handle and the submission it belongs to. Both live at
 * module scope because the page calls a free function per submission and cannot
 * carry the handle across calls itself.
 *
 * `currentSubmissionId` is assigned rather than maximized: the page increments
 * the id once per submission, so the last call to issue a request is by
 * definition the current one.
 */
let inFlightController: AbortController | null = null;
let currentSubmissionId = 0;

/**
 * Cancels whatever is in flight and records `submissionId` as the current one,
 * so a request already running settles as `superseded` rather than reporting
 * its rows. Every submission passes through here, whether it goes on to issue a
 * request or is refused before it can.
 */
function abandonInFlight(submissionId: number): void {
  inFlightController?.abort();
  inFlightController = null;
  currentSubmissionId = submissionId;
}

function refuse(outcome: DispatchOutcome): RequestPlan {
  return { kind: "refused", outcome };
}

function configErrorFor(lob: string, detail: string): RequestPlan {
  return refuse({
    kind: "configError",
    message: `Claims search is unavailable for line of business "${lob}": ${detail}`,
  });
}

/**
 * The term as the request carries it: trimmed first, so a normalization never
 * sees surrounding whitespace, then normalized as the field declares.
 *
 * The term arrives already accepted — the search bar refuses an empty,
 * over-length, or rule-violating term before a submission is recorded — so
 * there is nothing to re-check here.
 */
function conditionTerm(term: string, normalization: TermNormalization | undefined): string {
  const trimmed = term.trim();
  switch (normalization ?? "none") {
    case "lowercase":
      return trimmed.toLowerCase();
    case "uppercase":
      return trimmed.toUpperCase();
    default:
      return trimmed;
  }
}

/**
 * The epoch second at the start of a `YYYY-MM-DD` day in UTC, or null for a
 * value that is not one. UTC because the date columns resolve in UTC by
 * default, so a bound and a rendered date agree about which day a claim is on.
 *
 * The component round-trip is what rejects `2024-02-31` and `2024-13-01`:
 * `Date.UTC` rolls both over into a valid instant rather than refusing them, so
 * the pattern alone would accept a day that does not exist and search a
 * different one.
 */
function startOfUtcDay(isoDate: string): number | null {
  if (!ISO_DATE_PATTERN.test(isoDate)) {
    return null;
  }
  const [year, month, day] = isoDate.split("-").map(Number);
  const utcMilliseconds = Date.UTC(year, month - 1, day);
  const asDate = new Date(utcMilliseconds);
  if (
    asDate.getUTCFullYear() !== year ||
    asDate.getUTCMonth() !== month - 1 ||
    asDate.getUTCDate() !== day
  ) {
    return null;
  }
  return Math.floor(utcMilliseconds / 1000);
}

type DateBoundsResolution =
  | { readonly kind: "none" }
  | { readonly kind: "bounds"; readonly startSecond: number; readonly endSecond: number }
  | { readonly kind: "invalid"; readonly message: string };

/**
 * The selected range as two epoch seconds spanning whole days: the first second
 * of the start date and the last second of the end date, so a single-day range
 * covers the whole day rather than collapsing to one instant.
 *
 * The end bound is not clamped to now. The picker already rejects future dates,
 * so the only range a clamp ever touched was one ending today, and no claim
 * carries a future timestamp.
 *
 * A relative picker selection never reaches here: the range arrives as two
 * absolute calendar dates, resolved when the submission was recorded.
 */
function resolveDateBounds(dateRange: SearchDateRange | null): DateBoundsResolution {
  const start = dateRange?.start.trim() ?? "";
  const end = dateRange?.end.trim() ?? "";

  if (start === "" && end === "") {
    return { kind: "none" };
  }
  if (start === "" || end === "") {
    return {
      kind: "invalid",
      message: "Select both a date range start and a date range end, or clear the date range.",
    };
  }

  const startSecond = startOfUtcDay(start);
  const endDayStartSecond = startOfUtcDay(end);
  if (startSecond === null || endDayStartSecond === null) {
    return { kind: "invalid", message: "Select a date range whose start and end are both dates." };
  }
  if (startSecond > endDayStartSecond) {
    return {
      kind: "invalid",
      message: "Select a date range whose start date is not later than its end date.",
    };
  }

  return {
    kind: "bounds",
    startSecond,
    endSecond: endDayStartSecond + SECONDS_PER_DAY - 1,
  };
}

/**
 * The country entry for `queryInput`, or nothing.
 *
 * An empty selection sends no country key rather than an empty set: a set with
 * no members matches no claim, which would render an empty table for a user who
 * has simply cleared the control. The page widens a cleared selection to every
 * country the roles grant before calling, so an empty array here means there is
 * no country scoping to apply.
 */
function countryInput(config: SearchConfig, countries: readonly string[]): QueryInput {
  const filter = config.countryFilter;
  if (!filter || countries.length === 0) {
    return {};
  }
  return { [filter.queryInputProperty]: countries };
}

function planMySubmittedClaims(args: ExecuteSearchArgs, alias: string): RequestPlan {
  const { config, countries } = args;
  const mode = config.mySubmittedClaims;

  if (!mode) {
    return configErrorFor(
      config.lob,
      "its search configuration declares no my-submitted-claims query."
    );
  }
  if (!mode.queryName) {
    return configErrorFor(
      config.lob,
      "its my-submitted-claims configuration declares no queryName."
    );
  }

  const trimmedAlias = alias.trim();
  if (trimmedAlias === "") {
    return refuse({
      kind: "validationError",
      message: "Your submitted claims cannot be listed: the signed-in user's alias is unavailable.",
    });
  }

  return {
    kind: "dispatchable",
    request: {
      claimType: config.claimType,
      queryName: mode.queryName,
      queryInput: {
        [mode.queryInputProperty]: trimmedAlias,
        ...countryInput(config, countries),
      },
      pageSize: mode.pageSize,
    },
  };
}

function planFieldSearch(args: ExecuteSearchArgs, field: SearchableField): RequestPlan {
  const { config, state, countries } = args;

  if (!field.queryName) {
    return configErrorFor(config.lob, `searchable field "${field.fieldId}" declares no queryName.`);
  }

  const queryInput: QueryInput = {
    [field.queryInputProperty]: conditionTerm(state.term, field.normalization),
    ...countryInput(config, countries),
  };

  if (field.acceptsDateRange) {
    const dateRangeRequest = config.dateRangeRequest;

    // A field that accepts a range against a claim type whose queries accept no
    // bounds. Refusing is the point: a request without the bounds would answer
    // a different question than the one asked and look like the right answer.
    // Not a defective config — it is the state until the backend can serve the
    // bounded query — so it is not a configError.
    if (!dateRangeRequest) {
      return refuse({
        kind: "unsupportedQuery",
        message:
          `Date-bounded search is unavailable: "${field.labels.buttonText}" searches over a date ` +
          "range, and the claims service cannot accept date range bounds yet. Select a different " +
          "search field to continue.",
      });
    }

    const bounds = resolveDateBounds(state.dateRange);
    if (bounds.kind === "invalid") {
      return refuse({ kind: "validationError", message: bounds.message });
    }
    if (bounds.kind === "bounds") {
      queryInput[dateRangeRequest.startQueryInputProperty] = bounds.startSecond;
      queryInput[dateRangeRequest.endQueryInputProperty] = bounds.endSecond;
    }
  }

  return {
    kind: "dispatchable",
    request: {
      claimType: config.claimType,
      queryName: field.queryName,
      queryInput,
      pageSize: config.retrievalPageSize,
    },
  };
}

function planRequest(args: ExecuteSearchArgs): RequestPlan {
  const { config, state, mySubmittedClaimsAlias } = args;

  if (!config.claimType) {
    return configErrorFor(config.lob, "its search configuration declares no claimType.");
  }

  if (mySubmittedClaimsAlias !== undefined) {
    return planMySubmittedClaims(args, mySubmittedClaimsAlias);
  }

  const field = config.searchableFields.find(
    (candidate) => candidate.fieldId === state.selectedFieldId
  );
  if (!field) {
    return configErrorFor(
      config.lob,
      `its search configuration declares no searchable field "${state.selectedFieldId}".`
    );
  }

  return planFieldSearch(args, field);
}

/**
 * Field-level errors carried by a rejection, or null when it carries none.
 *
 * Only entries holding a message are returned: an entry without one has nothing
 * to show against its field, and reporting it as a field error would render a
 * named field beside a blank explanation.
 */
function readFieldErrors(error: unknown): readonly ErrorDetails[] | null {
  if (typeof error !== "object" || error === null) {
    return null;
  }
  const candidates: unknown = (error as { errors?: unknown }).errors;
  if (!Array.isArray(candidates)) {
    return null;
  }
  const errors = (candidates as readonly unknown[]).filter(
    (candidate): candidate is ErrorDetails =>
      typeof candidate === "object" &&
      candidate !== null &&
      typeof (candidate as { errorMessage?: unknown }).errorMessage === "string"
  );
  return errors.length > 0 ? errors : null;
}

/**
 * Issues one request, aborting the previous submission's first so a superseded
 * search stops consuming a Lambda invocation instead of running to completion,
 * and races it against the timeout.
 *
 * Staleness is checked on both settlement paths before anything is reported. A
 * request aborted by a newer submission rejects with an AbortError, which is
 * indistinguishable from a real failure at this boundary, so without the check
 * a superseded search would render as a retrieval error.
 */
async function issueRequest(
  request: DispatchableRequest,
  submissionId: number
): Promise<DispatchOutcome> {
  abandonInFlight(submissionId);
  const controller = new AbortController();
  inFlightController = controller;

  let timer: ReturnType<typeof setTimeout> | undefined;
  const expiry = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new Error(`Claims retrieval did not complete within ${RETRIEVAL_TIMEOUT_MS} ms.`));
    }, RETRIEVAL_TIMEOUT_MS);
  });

  try {
    const outcome = await Promise.race([
      retrieveClaims({ ...request, signal: controller.signal }),
      expiry,
    ]);
    if (submissionId < currentSubmissionId) {
      return { kind: "superseded" };
    }
    return { kind: "results", rows: outcome.rows, hasMoreResults: outcome.hasMoreResults };
  } catch (error: unknown) {
    if (submissionId < currentSubmissionId) {
      return { kind: "superseded" };
    }
    const fieldErrors = readFieldErrors(error);
    return fieldErrors ? { kind: "fieldErrors", errors: fieldErrors } : { kind: "retrievalError" };
  } finally {
    clearTimeout(timer);
    if (inFlightController === controller) {
      inFlightController = null;
    }
  }
}

/**
 * Executes one submission and returns its outcome. Holds no state the page can
 * read and writes nothing to Redux: the retrieval returns its rows directly and
 * the page records them under the submission id it passed in.
 *
 * Four outcomes issue no request at all, and they are deliberately distinct
 * kinds because they resolve differently: a `configError` is a defect in the
 * shipped config, an `unsupportedQuery` is a capability the claims service does
 * not have yet, and a `validationError` is something the user can correct.
 */
export async function executeSearch(args: ExecuteSearchArgs): Promise<DispatchOutcome> {
  const plan = planRequest(args);
  if (plan.kind === "refused") {
    // A refusal is still a newer submission: the page bumps the id before
    // calling, so the user has moved on from whatever is in flight even though
    // this submission issues nothing. Abandoning it stops the earlier request
    // consuming a Lambda invocation, and keeps its rows from landing as the
    // answer to a query the user has since replaced.
    abandonInFlight(args.state.submissionId);
    return plan.outcome;
  }
  return await issueRequest(plan.request, args.state.submissionId);
}
